import { css } from '@emotion/react';
import { PropsWithChildren, Children } from 'react';
import { getMediaUp, getMediaDown, getMediaBetween } from './breakpoints';

interface childrenTypes {
  1?: string | undefined;
  2?: string | undefined;
  3?: string | undefined;
  4?: string | undefined;
  5?: string | undefined;
  6?: string | undefined;
  7?: string | undefined;
  8?: string | undefined;
  9?: string | undefined;
  10?: string | undefined;
  11?: string | undefined;
  12?: string | undefined;
  optional?: string | undefined;
}

interface childGridsTypes {
  0?: childrenTypes;
  1?: childrenTypes;
  2?: childrenTypes;
  3?: childrenTypes;
  4?: childrenTypes;
}

const gridOrganizers = (
  breakpoint: number,
  gridCols: Array<string>,
  gridRows?: Array<string>,
  customChildGrids?: childGridsTypes
) => {
  const customChildGridsBreakpoint = customChildGrids
    ? customChildGrids[breakpoint as keyof childGridsTypes]
    : false;

  return `
  grid-template-columns: ${gridCols[breakpoint]};
  ${
    gridRows && gridRows[breakpoint]
      ? `grid-template-rows: ${gridRows[breakpoint]};`
      : ``
  }
  ${
    customChildGrids && customChildGridsBreakpoint
      ? Object.keys(customChildGridsBreakpoint)
          .map(
            (child) => `
          > *:nth-of-type(${child}) {
            ${
              customChildGridsBreakpoint[
                child as keyof typeof customChildGridsBreakpoint
              ]
            };
          }
        `
          )
          .join('')
      : ``
  }`;
};

const gridStyles = (
  gridCols: Array<string>,
  gridRows?: Array<string>,
  customChildGrids?: childGridsTypes
) => css`
  display: grid;
  justify-content: space-between;
  margin: auto;
  max-width: 100%;
  box-sizing: border-box;

  ${getMediaDown('lg')} {
    gap: 24px;
    padding-left: 32px;
    padding-right: 32px;
    & + .row-layout {
      margin-top: 24px;
    }
  }

  ${getMediaUp('lg')} {
    gap: 32px;
    padding-left: 60px;
    padding-right: 60px;
    & + .row-layout {
      margin-top: 32px;
    }
  }

  ${getMediaDown('sm')} {
    width: 328px;
    ${gridOrganizers(0, gridCols, gridRows, customChildGrids)}
  }

  ${getMediaBetween('sm', 'md')} {
    width: 508px;
    ${gridOrganizers(1, gridCols, gridRows, customChildGrids)}
  }

  ${getMediaBetween('md', 'lg')} {
    width: 768px;
    ${gridOrganizers(2, gridCols, gridRows, customChildGrids)}
  }

  ${getMediaBetween('lg', 'xl')} {
    width: 1024px;
    ${gridOrganizers(3, gridCols, gridRows, customChildGrids)}
  }

  ${getMediaUp('xl')} {
    width: 1168px;
    ${gridOrganizers(4, gridCols, gridRows, customChildGrids)}
  }
`;

const LayoutStyles = {
  '12': { style: gridStyles(Array(5).fill('minmax(0, 1fr)')), count: 1 },
  '6-6': {
    style: gridStyles([
      'minmax(0, 1fr)',
      'minmax(0, 1fr)',
      'minmax(0, 1fr) minmax(0, 1fr)',
      'minmax(0, 1fr) minmax(0, 1fr)',
      'minmax(0, 1fr) minmax(0, 1fr)'
    ]),
    count: 2
  },
  '4-4-4': {
    style: gridStyles([
      'minmax(0, 1fr)',
      'minmax(0, 1fr)',
      'repeat(3, minmax(0, 1fr))',
      'repeat(3, minmax(0, 1fr))',
      'repeat(3, minmax(0, 1fr))'
    ]),
    count: 3
  },
  '3-3-3-3': {
    style: gridStyles([
      'minmax(0, 1fr)',
      'minmax(0, 1fr) minmax(0, 1fr)',
      'minmax(0, 1fr) minmax(0, 1fr)',
      'repeat(4, minmax(0, 1fr))',
      'repeat(4, minmax(0, 1fr))'
    ]),
    count: 4
  },
  '2-2-2-2-2-2': {
    style: gridStyles([
      'minmax(0, 1fr) minmax(0, 1fr)',
      'minmax(0, 1fr) minmax(0, 1fr)',
      'minmax(0, 1fr) minmax(0, 1fr)',
      'repeat(6, minmax(0, 1fr))',
      'repeat(6, minmax(0, 1fr))'
    ]),
    count: 6
  },
  '4-8': {
    style: gridStyles([
      'minmax(0, 1fr)',
      'minmax(0, 1fr)',
      'minmax(0, 1fr) minmax(0, 2fr)',
      'minmax(0, 1fr) minmax(0, 2fr)',
      'minmax(0, 1fr) minmax(0, 2fr)'
    ]),
    count: 2
  },
  '3-9': {
    style: gridStyles([
      'minmax(0, 1fr)',
      'minmax(0, 1fr)',
      'minmax(0, 1fr)',
      'minmax(0, 1fr) minmax(0, 3fr)',
      'minmax(0, 1fr) minmax(0, 3fr)'
    ]),
    count: 2
  },
  '8-4': {
    style: gridStyles([
      'minmax(0, 1fr)',
      'minmax(0, 1fr)',
      'minmax(0, 2fr) minmax(0, 1fr)',
      'minmax(0, 2fr) minmax(0, 1fr)',
      'minmax(0, 2fr) minmax(0, 1fr)'
    ]),
    count: 2
  },
  '9-3': {
    style: gridStyles([
      'minmax(0, 1fr)',
      'minmax(0, 1fr)',
      'minmax(0, 1fr)',
      'minmax(0, 3fr) minmax(0, 1fr)',
      'minmax(0, 3fr) minmax(0, 1fr)'
    ]),
    count: 2
  },
  '3-3-6': {
    style: gridStyles(
      [
        'minmax(0, 1fr)',
        'minmax(0, 1fr) minmax(0, 1fr)',
        'minmax(0, 1fr) minmax(0, 1fr)',
        'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 2fr)',
        'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 2fr)'
      ],
      [
        'auto',
        'minmax(0, 1fr) minmax(0, auto)',
        'minmax(0, 1fr) minmax(0, auto)',
        'auto',
        'auto'
      ],
      [
        {},
        {
          3: `grid-area: 2 / 1 / 3 / 3;`
        },
        {
          3: `grid-area: 2 / 1 / 3 / 3;`
        },
        {},
        {}
      ]
    ),
    count: 3
  },
  '6-3-3': {
    style: gridStyles(
      [
        'minmax(0, 1fr)',
        'minmax(0, 1fr) minmax(0, 1fr)',
        'minmax(0, 1fr) minmax(0, 1fr)',
        'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)',
        'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)'
      ],
      [
        'auto',
        'minmax(0, auto) minmax(0, 1fr)',
        'minmax(0, auto) minmax(0, 1fr)',
        'auto',
        'auto'
      ],
      [
        {},
        {
          1: `grid-area: 1 / 1 / 1 / 3;`,
          2: `grid-area: 2 / 1 / 3 / 2;`,
          3: `grid-area: 2 / 2 / 3 / 3;`
        },
        {
          1: `grid-area: 1 / 1 / 1 / 3;`,
          2: `grid-area: 2 / 1 / 3 / 2;`,
          3: `grid-area: 2 / 2 / 3 / 3;`
        },
        {},
        {}
      ]
    ),
    count: 3
  }
};

type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];

const getEntries = <T extends object>(obj: T) =>
  Object.entries(obj) as Entries<T>;

const DefaultStyles = Object.fromEntries(
  getEntries(LayoutStyles)
    .slice(0, 5)
    .map(([key, val]) => [val['count'], key])
);

Object.freeze(LayoutStyles);
Object.freeze(DefaultStyles);

type layout = keyof typeof LayoutStyles;

const verticalAlignment = {
  default: 'stretch',
  top: 'start',
  bottom: 'end',
  center: 'center'
};

type AlignTypes = 'default' | 'top' | 'bottom' | 'center';

export interface RowProps {
  layout?: layout;
  alignItems?: AlignTypes;
}

const getLayoutStyles = (childCount: number, layout?: layout) => {
  if (layout === undefined) {
    layout = DefaultStyles[childCount];
    if (layout === undefined) {
      throw new Error(`${childCount} is an invalid number of children`);
    }
  }
  if (childCount != LayoutStyles[layout]['count']) {
    throw new Error(
      `Row with layout "${layout}" should have ${LayoutStyles[layout]['count']} children`
    );
  }

  return LayoutStyles[layout]['style'];
};

export const Row = (props: PropsWithChildren<RowProps>) => {
  const { layout } = props;
  const { alignItems } = props;
  const children = Children.toArray(props.children);
  const style = getLayoutStyles(children.length, layout);

  return (
    <div
      className="row-layout"
      css={css`
        ${style}
        align-items: ${alignItems
          ? verticalAlignment[alignItems]
          : verticalAlignment.default};
      `}
    >
      {children.map((child) => child)}
    </div>
  );
};
